import { useTranslation } from "react-i18next"

import TiktokIcon from "@/components/social-accounts/tiktok-icon"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export interface TiktokVerifiedBadgeProps {
  /** Server-shipped flag (FLAT field on creator/candidate detail props,
   *  per BE TRD §"Existing CandidateShowProps gain"). FE renders nothing
   *  when `false` — the badge is only meaningful for active links. */
  verified: boolean
  className?: string
}

/**
 * TiktokVerifiedBadge — small "Verified" pill rendered next to a
 * creator's TikTok handle on brand/agency-facing creator + candidate
 * detail pages.
 *
 * UX Spec §1.E + CTPO ship-it 2026-05-01:
 *   - Wording: **"Verified"** (NOT "TikTok-verified" — the glyph
 *     carries TikTok context implicitly).
 *   - Visual: shadcn `<Badge>` (default variant) + small TikTok glyph
 *     prefix.
 *   - Tooltip on hover/focus: "OAuth-verified TikTok account".
 *   - Renders ONLY when `verified === true`. Negative-test in Vitest:
 *     `verified={false}` → component renders nothing.
 *
 * a11y:
 *   - Visible "Verified" text + TikTok glyph; sr-only badge label
 *     ("Verified TikTok account") for AT clarity.
 *   - Tooltip is keyboard-accessible (Radix wraps focus + Esc).
 */
export function TiktokVerifiedBadge({
  verified,
  className = "",
}: TiktokVerifiedBadgeProps) {
  const { t } = useTranslation()
  if (!verified) return null
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger
          render={
            <Badge
              variant="secondary"
              data-testid="tiktok-verified-badge"
              className={[
                // Compact pill — tone matches the existing role/tier
                // badge family used on candidate detail.
                "inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium",
                className,
              ].join(" ")}
              aria-label={t("tiktok.verified_badge.aria_label")}
            >
              <TiktokIcon width={12} height={12} fill="currentColor" />
              <span>{t("tiktok.verified_badge.label")}</span>
            </Badge>
          }
        />
        <TooltipContent data-testid="tiktok-verified-badge-tooltip" side="top">
          {t("tiktok.verified_badge.tooltip")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default TiktokVerifiedBadge
